import { Check, CircleDashed, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrderEvents } from "@/hooks/use-order-events";
import { type OrderStatus, useOrder } from "@/hooks/use-orders";
import { formatPrice } from "@/lib/format-price";

const STATUS_FLOW: OrderStatus[] = ["pending", "accepted", "in_progress", "ready", "delivered"];

function statusIndex(status: OrderStatus): number {
  const idx = STATUS_FLOW.indexOf(status);
  return idx === -1 ? 0 : idx;
}

export default function OrderStatusPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useOrder(id);
  useOrderEvents(id ? { type: "order", orderId: id } : { type: "org" });

  if (isLoading) {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-2xl flex-col gap-6 px-4 py-12">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-2xl items-center justify-center px-4 py-12">
        <div className="space-y-3 text-center">
          <h1 className="font-semibold text-xl">
            {t("orders.notFound", { defaultValue: "Pedido no encontrado" })}
          </h1>
          <p className="text-muted-foreground text-sm">{error?.message ?? ""}</p>
        </div>
      </div>
    );
  }

  const { order, items } = data;
  const status = order.status as OrderStatus;
  const isPublicCatalogOrder = order.origin === "public_catalog";
  const isTerminal = status === "rejected" || status === "cancelled" || status === "delivered";
  const stageIndex = statusIndex(status);

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-2xl flex-col gap-6 px-4 py-12">
      <header className="space-y-2">
        <h1 className="font-semibold font-serif text-3xl tracking-tight">
          {t("orders.status.title", { defaultValue: "Tu pedido" })}
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">
            {t(`orders.status.${status}`, {
              defaultValue: status.replace("_", " "),
            })}
          </Badge>
          <Badge variant="outline">
            {t(`orders.payment.${order.paymentStatus}`, {
              defaultValue: order.paymentStatus,
            })}
          </Badge>
          {order.paymentMode === "pay_later" ? (
            <Badge>
              {isPublicCatalogOrder
                ? t("orders.payment.payOnDelivery", {
                    defaultValue: "Se paga al recibir",
                  })
                : t("orders.payment.payAtCounter", {
                    defaultValue: "Se paga al entregar",
                  })}
            </Badge>
          ) : null}
        </div>
      </header>

      {status === "rejected" ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
          <h2 className="font-semibold text-destructive text-sm">
            {t("orders.status.rejectedTitle", {
              defaultValue: "Pedido rechazado",
            })}
          </h2>
          {order.rejectionReason ? <p className="mt-1 text-sm">{order.rejectionReason}</p> : null}
        </div>
      ) : null}

      {status === "cancelled" ? (
        <div className="rounded-lg border border-border bg-muted p-4 text-sm">
          {t("orders.status.cancelled", {
            defaultValue: "Este pedido fue cancelado.",
          })}
        </div>
      ) : null}

      {!isTerminal || status === "delivered" ? (
        <ol className="space-y-3">
          {STATUS_FLOW.map((step, index) => {
            const reached = index <= stageIndex;
            const Icon = reached ? Check : index === stageIndex + 1 ? Clock : CircleDashed;
            return (
              <li
                className={`flex items-center gap-3 rounded-lg border p-3 ${reached ? "border-primary/40 bg-primary/5" : "border-border"}`}
                key={step}
              >
                <Icon className={`h-5 w-5 ${reached ? "text-primary" : "text-muted-foreground"}`} />
                <span className="font-medium text-sm">
                  {t(`orders.status.${step}`, {
                    defaultValue: step.replace("_", " "),
                  })}
                </span>
              </li>
            );
          })}
        </ol>
      ) : null}

      <section className="space-y-2 rounded-lg border border-border bg-card p-4">
        <h2 className="font-semibold text-sm uppercase tracking-wide">
          {t("orders.status.items", { defaultValue: "Items" })}
        </h2>
        <ul className="space-y-2">
          {items.map((item) => (
            <li className="flex items-center justify-between text-sm" key={item.id}>
              <span>
                {item.quantity} × {item.nameSnapshot}
              </span>
              <span className="tabular-nums">
                {formatPrice(item.priceSnapshot * item.quantity, order.currency)}
              </span>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between border-border border-t pt-2 font-semibold">
          <span>{t("orders.status.total", { defaultValue: "Total" })}</span>
          <span className="tabular-nums">{formatPrice(order.total, order.currency)}</span>
        </div>
      </section>
    </div>
  );
}
